"use client";

import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, TrendingUp, Key } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";

export default function OwnerCTA() {
  return (
    <section className="relative py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-3xl sm:rounded-[2.5rem] overflow-hidden shadow-2xl w-full min-h-[460px] sm:min-h-[520px] flex items-center bg-black"
        >
          {/* Full Width Background Image (The Requested Image) */}
          <div className="absolute inset-0 w-full h-full">
            <Image
              src="/images/bg-villa-1.webp"
              alt="Young Property Owner Thinking at Luxury Villa"
              title="Young Property Owner Thinking at Luxury Villa"
              fill
              quality={100}
              className="object-cover transition-transform duration-1000 hover:scale-105"
            />
          </div>

          {/* Mobile Dark Overlay ensuring readability */}
          <div className="absolute inset-0 bg-black/60 md:hidden z-10 w-full"></div>

          {/* Desktop Left-to-Right Dark Gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-transparent z-10 hidden md:block w-full md:w-[80%] lg:w-[60%]"></div>

          {/* Content Section (Glass Card) */}
          <div className="relative z-20 p-6 sm:p-8 md:p-14 lg:p-20 w-full md:w-4/5 lg:w-3/5 xl:w-1/2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="mb-4 sm:mb-6"
            >
              <span className="inline-flex items-center gap-1.5 sm:gap-2 py-1.5 px-3 sm:px-4 rounded-full bg-white/10 backdrop-blur-md text-white text-xs sm:text-sm font-semibold tracking-wider border border-white/20 shadow-md">
                <Key className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                FOR PROPERTY OWNERS
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.2] sm:leading-[1.1] text-white drop-shadow-lg mb-4 sm:mb-6"
            >
              Are you looking to give your house for rental?
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-base sm:text-lg md:text-xl text-white/90 sm:text-white/80 mb-8 sm:mb-10 leading-relaxed max-w-md drop-shadow-md font-light"
            >
              Let HOPO Homes handle the hard work. We find verified tenants,
              manage maintenance, and guarantee consistent returns with zero
              hassle for you.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 w-full"
            >
              <Link
                title="Rent your Property"
                href="/owner"
                className="w-full rounded-full text-sm sm:text-base px-6 sm:px-8 py-6 sm:py-7 bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl hover:scale-105 transition-transform duration-300 flex items-center justify-center gap-2 font-bold"
              >
                Rent your Property
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1" />
              </Link>
            </motion.div>
          </div>

          {/* Floating Badges for large screens only */}
          <div className="absolute right-8 bottom-12 hidden lg:flex flex-col gap-6 z-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="bg-black/40 backdrop-blur-xl border border-white/10 p-4 rounded-2xl flex items-center gap-4 w-[240px] transform hover:scale-105 transition-all shadow-2xl"
            >
              <div className="bg-primary/20 p-3 rounded-xl flex items-center justify-center border border-primary/30">
                <ShieldCheck className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-base tracking-wide leading-tight">
                  Zero Hassle
                </h3>
                <p className="text-white/70 text-xs">
                  Full property management
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
